import { useEffect, useState } from "react";

import { trpc } from "@calcom/trpc/react";
import { Button, showToast } from "@calcom/ui";

import AddPatientDialog from "@components/dialog/AddPatientDialog";
import { useOfficeContext } from "@components/navbar";

export type CreatedPatientOption = {
  patientId: number;
  firstName: string;
  lastName: string;
};

type MissingPatientPromptProps = {
  officeId: number;
  visible: boolean;
  onPatientCreated?: (patient: CreatedPatientOption) => void;
};

export function MissingPatientPrompt({ officeId, visible, onPatientCreated }: MissingPatientPromptProps) {
  const { office, isOfficeLoading } = useOfficeContext();
  const utils = trpc.useContext();
  const [addPatientOpen, setAddPatientOpen] = useState(false);
  const [hasSynced, setHasSynced] = useState(false);

  const officeMatches = office?.id === officeId;
  const isNexHealthIntegrated = Boolean(officeMatches && office?.isNexHealthIntegrated);
  const integrationKnown = officeMatches && !isOfficeLoading;

  useEffect(() => {
    setHasSynced(false);
  }, [officeId]);

  const syncPatients = trpc.viewer.patients.syncNexhealthPatients.useMutation({
    onSuccess: async (result) => {
      await utils.viewer.patients.getNameList.invalidate();
      setHasSynced(true);
      if ("message" in result) {
        showToast(result.message, "error");
        return;
      }
      const created = result.report.reduce((total, row) => total + row.created, 0);
      showToast(
        created > 0
          ? `Patient sync finished. ${created} new patient${created === 1 ? "" : "s"} added.`
          : "Patient sync finished. Search again if you still can't find the patient.",
        "success"
      );
    },
    onError: (error) => {
      setHasSynced(true);
      showToast(error.message || "Patient sync failed", "error");
    },
  });

  if (!visible || !integrationKnown) return null;

  const showCreate = !isNexHealthIntegrated || hasSynced;

  return (
    <>
      <div className="mt-2 space-y-2">
        <p className="text-sm text-gray-600">Can&apos;t you find a patient?</p>
        <div className="flex flex-wrap gap-2">
          {isNexHealthIntegrated && (
            <Button
              type="button"
              color="secondary"
              size="sm"
              className="border bg-white text-gray-600 hover:bg-white hover:text-gray-600"
              loading={syncPatients.isLoading}
              onClick={() => syncPatients.mutate({ officeId })}>
              Sync Patients
            </Button>
          )}
          {showCreate && (
            <Button
              type="button"
              color="secondary"
              size="sm"
              className="border bg-white text-gray-600 hover:bg-white hover:text-gray-600"
              onClick={() => setAddPatientOpen(true)}>
              Create patient manually
            </Button>
          )}
        </div>
      </div>
      {addPatientOpen && (
        <AddPatientDialog
          open={addPatientOpen}
          handler={setAddPatientOpen}
          officeId={officeId}
          onCreated={(patient) => {
            onPatientCreated?.(patient);
          }}
        />
      )}
    </>
  );
}
